import { apiClient } from "../config/axios";

export interface Session {
    id: string;
    userAgent?: string | null;
    clientId: string;
    createdAt: string;
    updatedAt: string;
}

export interface PaginatedSessions {
    data: Session[];
    total: number;
    page: number;
    limit: number;
}

export type ClientLanguage = "fr" | "en" | "lb";

export interface Client {
    id: string;
    name: string;
    displayName?: string;
    layoutColor?: string;
    type?: string;
    language?: ClientLanguage;
    apiKey?: string;
    allowedOrigins?: string[];
    userId: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
}

export interface PublicClient {
    id: string;
    name: string;
    displayName?: string;
    layoutColor?: string;
    type?: string;
    language?: ClientLanguage;
}

export interface CreateClientDto {
    name: string;
    displayName?: string;
    layoutColor?: string;
    type?: string;
    language?: ClientLanguage;
    allowedOrigins?: string[];
}

export interface UpdateClientDto {
    name?: string;
    displayName?: string;
    layoutColor?: string;
    type?: string;
    language?: ClientLanguage;
    allowedOrigins?: string[];
}

const BLOCKED_HOSTS = ["localhost", "127.0.0.1", "0.0.0.0", "::1"];

export function validateOrigin(origin: string): string | null {
    try {
        const { hostname } = new URL(origin);
        if (BLOCKED_HOSTS.includes(hostname) || hostname.endsWith(".localhost")) {
            return `Local origins are not allowed: ${origin}`;
        }
        return null;
    } catch {
        return `Invalid URL: ${origin}`;
    }
}

export const clientService = {
    async create(data: CreateClientDto): Promise<Client> {
        const res = await apiClient.post<Client>("/v1/clients", data);
        return res.data;
    },

    async getAll(): Promise<Client[]> {
        const res = await apiClient.get<Client[]>("/v1/clients");
        return res.data;
    },

    async getById(id: string, signal?: AbortSignal): Promise<Client> {
        const res = await apiClient.get<Client>(`/v1/clients/${id}`, { signal });
        return res.data;
    },

    async getPublicById(id: string): Promise<PublicClient> {
        const res = await apiClient.get<PublicClient>(`/v1/clients/${id}/public`);
        return res.data;
    },

    async update(id: string, data: UpdateClientDto): Promise<Client> {
        const res = await apiClient.patch<Client>(`/v1/clients/${id}`, data);
        return res.data;
    },

    async delete(id: string): Promise<void> {
        await apiClient.delete(`/v1/clients/${id}`);
    },

    async getSessions(id: string, page = 1, limit = 20): Promise<PaginatedSessions> {
        const res = await apiClient.get<PaginatedSessions>(
            `/v1/clients/${id}/sessions`, { params: { page, limit } }
        );
        return res.data;
    },

    async regenerateApiKey(id: string): Promise<{ apiKey: string }> {
        const res = await apiClient.post<{ apiKey: string }>(`/v1/clients/${id}/regenerate-key`);
        return res.data;
    },

    async generateDevKey(id: string): Promise<{ devApiKey: string }> {
        const res = await apiClient.post<{ devApiKey: string }>(`/v1/clients/${id}/generate-dev-key`);
        return res.data;
    },
};
